import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { Box, Tooltip, Typography } from '@mui/material';
import EnergyMark from '../components/datagrid/EnergyMark';
import KpiGauge from '../components/datagrid/KpiGauge';

import { useGetMatrikelAnalysisQuery } from '@/api/energy-analysis/portfolioAnalysisApiSlice';
import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import OccupancyDaysCell from '@/features/portfolio-analysis/components/datagrid/OccupancyDaysCell';
import TypeIcon from '@/features/portfolio-analysis/components/datagrid/TypeIcon';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { green, orange, red, yellow } from '@mui/material/colors';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatNumber } from '../../../utils/formatters';
import {
  calculatePercentiles,
  CustomHeaderWithLabel,
  displayTime,
  isGroupRow,
  isLeafRow,
  onlyRenderInParent,
  renderTrend,
  sortComparatorWhenGrouping,
  valueGetterWrapper,
} from '../utils/datagrid-helpers';
import DataIntegrityButton from '@/features/portfolio-analysis/components/data-integrity/DataIntegrityButton';

export const usePAColumns = (
  transformedData,
  analysisType,
  presets,
  chosenPreset,
) => {
  const account = useSelector(getSelectedAccount);
  //Matrikel analysis
  const { data: matrikelAnalysisData } = useGetMatrikelAnalysisQuery(
    {
      recalc_account_id: account?.AccountId,
    },
    {
      skip: !account?.AccountId,
    },
  );

  const { t } = useTranslation(`portfolio-analysis_${analysisType}`);
  const firstColumns = useMemo(() => {
    let result = presets[chosenPreset].columnGroupingModel.map((group) => {
      return group.children[0]?.field;
    });
    result.push('row_actions');
    result.push('warnings');
    result.push('matrikel_actions');
    return result;
  }, [presets, chosenPreset]);

  // Pre calculated percentile values
  const night_day_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData.portfolio_analysis_list.map((row) => {
        return row.night_day_ratio;
      }),
    );
  }, [transformedData]);

  const weekend_weekday_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData.portfolio_analysis_list.map((row) => {
        return row.weekend_weekday_ratio;
      }),
    );
  }, [transformedData]);

  const weekend_night_weekday_night_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData.portfolio_analysis_list.map((row) => {
        return row.weekend_night_weekday_night_ratio;
      }),
    );
  }, [transformedData]);

  const inactive_active_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData.portfolio_analysis_list.map((row) => {
        return row.inactive_active_ratio;
      }),
    );
  }, [transformedData]);
  const columns = useMemo(() => {
    const cols = [
      {
        flex: 0,
        aggregable: false,
        disableColumnMenu: true,
        hideSortIcons: true,
        field: 'favorites',
        type: 'boolean',
        minWidth: 50,
        width: 50,
        renderCell: (params) => {
          if (
            params.rowNode.isAutoGenerated &&
            params.rowNode?.children?.length > 0
          ) {
            const allChildrenFavorited = params.rowNode.children.every(
              (childId) => {
                const child = params.api.getRow(childId);
                return child.favorites;
              },
            );

            return allChildrenFavorited ? (
              <StarRoundedIcon
                sx={{ minWidth: 50, cursor: 'pointer' }}
                color="warning"
              />
            ) : (
              <StarBorderRoundedIcon
                sx={{ minWidth: 50, cursor: 'pointer' }}
                color="primary"
              />
            );
          }

          if (params.rowNode.isAutoGenerated) return null;

          return !params.value ? (
            <StarBorderRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="primary"
            />
          ) : (
            <StarRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="warning"
            />
          );
        },
      },
      {
        field: 'type',
        headerName: 'SEU',
        minWidth: 50,
        width: 50,
        flex: 0,
        renderCell: (params) => <TypeIcon params={params} />,
      },
      {
        field: 'address',
        minWidth: 250,
        type: 'string',
        hideable: false,
        aggregable: false,
        valueGetter: (value, row) => {
          return row.DisplayName || value;
        },
      },
      {
        field: 'matrikel_key',
        type: 'string',
        aggregable: false,
        groupingValueGetter: (params) => {
          let name = '';
          if (params == null) return null;

          return transformedData.portfolio_analysis_list.filter((item) => {
            if (item.matrikel_key === params) {
              name = item.matrikel_key;
            }
            return item.matrikel_key === params;
          }).length > 1
            ? name
            : null;
        },
        hideable: false,
      },
      {
        field: 'main_address',
        type: 'string',
        aggregable: false,
        hideable: false,
      },
      {
        field: 'average_all',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 160,
      },
      {
        field: 'last_30_days_average',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 160,
      },
      {
        field: 'average_heating_season',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 140,
      },
      {
        field: 'average_non_heating_season',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 140,
      },
      {
        field: 'average_heating_season_active',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 210,
      },
      {
        field: 'average_heating_season_inactive',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 210,
      },
      {
        field: 'last_30_day_trend',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'heating_season_trend',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 140,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'heating_season_trend_active',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 210,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'heating_season_trend_inactive',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 210,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'average_all_inactive_hours',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_17_to_21',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_all_last_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'current_calendar_year_avg',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'last_calendar_year_avg',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_day',
        minWidth: 160,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'yearly_trend',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yearly_trend_active',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yearly_trend_inactive',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio_weekdays',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio_weekday_nights',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio_weekends',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value, 1) + '%';
          }
          return formatNumber(value, 1) + '%';
        },
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'total_consumption_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'total_consumption_last_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'total_consumption_previous_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'current_calendar_year_sum',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'last_calendar_year_sum',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'average_weekday_full_day',
        minWidth: 90,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(Number(params), 1);
        },
      },
      {
        field: 'average_weekday_day',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_weekday_night',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_weekend_day',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_weekend_night',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_weekend_full_day',
        minWidth: 90,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },

      {
        field: 'weekend_weekday_ratio',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 2);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = weekend_weekday_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: green[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: yellow[700], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: orange[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: red[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'night_day_ratio',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 2);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = night_day_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: green[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: yellow[700], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: orange[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: red[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'inactive_active_ratio',
        minWidth: 150,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 2);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = inactive_active_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: green[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: yellow[700], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: orange[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: red[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'average_night',
        minWidth: 80,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'weekend_night_weekday_night_ratio',
        minWidth: 130,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 2);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = weekend_night_weekday_night_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: green[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: yellow[700], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: orange[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon
                    fontSize="5px"
                    sx={{ color: red[800], mr: 1 }}
                  />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'occupancy',
        minWidth: 140,
        maxWidth: 140,
        type: 'string',
        aggregable: false,
        renderCell: (params) => <OccupancyDaysCell daysOfWeek={params.value} />,
      },
      {
        field: 'daytime_start',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'daytime_end',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'total_co2e_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'kWhPrYPrM2',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params);
        },
        minWidth: 180,
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.VerificationFlag ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'BenchmarkCDF',
        minWidth: 180,
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return 'bund ' + formatNumber(params * 100, 1) + '%';
        },
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            return (
              <Tooltip title={params.formattedValue}>
                <div>
                  <KpiGauge value={params.value} />
                </div>
              </Tooltip>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'MedianDiff',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params);
        },
        renderCell: onlyRenderInParent,
        aggregable: false,
      },
      {
        field: 'MeanDiff',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params);
        },
        renderCell: onlyRenderInParent,
        aggregable: false,
      },
      {
        field: 'ExtractedBuildingUse',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            if (row?.BuildingUsesWithArea == undefined) return null;

            let jsonObject = JSON.parse(
              row?.BuildingUsesWithArea?.replaceAll("'", '"'),
            );
            let result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: row?.overwrite?.ExtractedBuildingUse
                      ? 600
                      : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {params.formattedValue}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: row?.overwrite?.ExtractedBuildingUse
                        ? 600
                        : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {params.formattedValue}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value} m²
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'AverageBuildingYear',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.AverageBuildingYear ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
        valueFormatter: (value) => {
          return value?.toFixed(0);
        },
      },
      {
        field: 'SumBuildingArea',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.SumBuildingArea ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'ExtractedHeatInstallation',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }

            if (row?.HeatInstallationsWithArea == undefined) return null;

            let jsonObject = JSON.parse(
              row?.HeatInstallationsWithArea?.replaceAll("'", '"'),
            );
            let result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: row?.overwrite?.ExtractedHeatInstallation
                      ? 600
                      : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {params.formattedValue}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: row?.overwrite?.ExtractedHeatInstallation
                        ? 600
                        : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {params.formattedValue}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value} m²
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'EnergyLabelClassification',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }

            if (
              row.EnergyLabelCountOverview == null ||
              row.EnergyLabelClassification == null
            )
              return null;

            let jsonObject = JSON.parse(
              row?.EnergyLabelCountOverview?.replaceAll("'", '"'),
            );
            let result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <EnergyMark mark={row?.EnergyLabelClassification} />
                {result.length > 1 && (
                  <InfoTooltipIcon
                    title={
                      <>
                        <Typography variant="body1">
                          {t('tooltips.energymarksFound')}
                        </Typography>
                        {result.map((buildingType, index) => (
                          <div key={index}>
                            <Typography variant="body2">
                              {buildingType.key}: {buildingType.value}
                            </Typography>
                          </div>
                        ))}
                      </>
                    }
                  />
                )}
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
        type: 'string',
      },
      {
        field: 'warnings',
        type: 'actions',
        aggregable: false,
        hideable: false,
        groupable: false,
        minWidth: 60,
        maxWidth: 60,
        renderCell: (params) => {
          if (isGroupRow(params)) {
            const children = params.rowNode.children?.map((childId) => {
              const child = params.api.getRow(childId);
              return child;
            });
            if (children.some((r) => !r.meter_in_sync)) {
              return (
                <Tooltip title={t('warnings.rowOutdated')} placement="top">
                  <SyncRoundedIcon
                    color="info"
                    sx={{
                      cursor: 'pointer',
                      marginLeft: 1,
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(360deg)',
                        },
                        '100%': {
                          transform: 'rotate(0deg)',
                        },
                      },
                    }}
                  />
                </Tooltip>
              );
            } else {
              return null;
            }
          }

          if (!params.row.meter_in_sync) {
            return (
              <Tooltip title={t('warnings.rowOutdated')} placement="top">
                <SyncRoundedIcon
                  color="info"
                  sx={{
                    cursor: 'pointer',
                    marginLeft: 1,
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                  }}
                />
              </Tooltip>
            );
          }
          return <DataIntegrityButton row={params.row} />;
        },
      },
    ];

    return cols
      .map((column) => {
        const origValueGetter = column.valueGetter;
        return {
          flex: 1,
          type: 'number',
          minWidth: 140,
          groupable: false,
          ...column,
          renderHeader: (params) => (
            <CustomHeaderWithLabel props={params} t={t} key={t.ns} />
          ),
          headerName: t(`columns.${column.field}.header`),

          valueGetter: (value, row, column, api) => {
            return valueGetterWrapper(
              value,
              row,
              column,
              api,
              origValueGetter,
              matrikelAnalysisData,
              analysisType,
            );
          },
          cellClassName: firstColumns.includes(column.field)
            ? 'cell-left-border'
            : '',
        };
      })
      .filter((column) => {
        if (analysisType === 'water')
          return !['average_17_to_21'].includes(column.field);
        if (analysisType === 'heat')
          return !['average_17_to_21'].includes(column.field);
        return true;
      });
  }, [
    transformedData.portfolio_analysis_list,
    weekend_weekday_ratio_percentiles,
    t,
    night_day_ratio_percentiles,
    inactive_active_ratio_percentiles,
    weekend_night_weekday_night_ratio_percentiles,
    firstColumns,
    matrikelAnalysisData,
    analysisType,
  ]);

  return columns;
};
